import React, { useEffect, useState, useRef, useMemo } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { ScanBarcode, Plus, Minus, Trash2, Send, IndianRupee, X, Search, UserPlus, Tag, Scissors, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import { GroomingConfigurator } from "./Grooming";

const inr = (n) => `₹${Number(n || 0).toFixed(2)}`;

export default function POS() {
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [groomingRates, setGroomingRates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [taxEnabled, setTaxEnabled] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [cart, setCart] = useState([]);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("products");
  const [payOpen, setPayOpen] = useState(false);
  const [custDialog, setCustDialog] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const barcodeRef = useRef(null);

  useEffect(() => {
    api.get("/products").then(r => setProducts(r.data));
    api.get("/services").then(r => setServices(r.data));
    api.get("/grooming/rates").then(r => setGroomingRates(r.data)).catch(() => {});
    api.get("/customers").then(r => setCustomers(r.data));
    api.get("/settings/tax").then(r => setTaxEnabled(r.data?.enabled !== false)).catch(() => {});
    barcodeRef.current?.focus();
  }, []);

  const refocus = () => setTimeout(() => barcodeRef.current?.focus(), 50);

  const onBarcode = async (e) => {
    if (e.key !== "Enter") return;
    const code = e.target.value.trim();
    if (!code) return;
    try {
      const { data } = await api.get(`/products/by-barcode/${encodeURIComponent(code)}`);
      addProduct(data);
      e.target.value = "";
    } catch {
      toast.error("Product not found for barcode");
      e.target.value = "";
    }
  };

  const addProduct = (p) => {
    if (p.stock <= 0) {
      toast.error(`${p.name} is out of stock`);
      return;
    }
    setCart(prev => {
      const idx = prev.findIndex(i => i.kind === "product" && i.ref_id === p.id);
      if (idx >= 0) {
        const copy = [...prev];
        if (copy[idx].qty + 1 > p.stock) { toast.warning("Stock limit reached"); return prev; }
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + 1 };
        return copy;
      }
      return [...prev, {
        lid: `p-${p.id}-${Date.now()}`,
        kind: "product", ref_id: p.id, name: p.name, hsn_code: p.hsn_code,
        qty: 1, unit_price: p.sale_price, discount_percent: 0, gst_percent: p.gst_percent, stock: p.stock,
      }];
    });
    refocus();
  };

  const addService = (s) => {
    setCart(prev => [...prev, {
      lid: `s-${s.id}-${Date.now()}`,
      kind: "service", ref_id: s.id, name: s.name, hsn_code: null,
      qty: 1, unit_price: s.price, discount_percent: 0, gst_percent: s.gst_percent,
    }]);
    refocus();
  };

  const addCustomItem = (item) => {
    setCart(prev => [...prev, {
      lid: `c-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      kind: "custom",
      ref_id: `custom-${Date.now()}`,
      name: item.name,
      hsn_code: null,
      qty: Math.max(1, Number(item.qty) || 1),
      unit_price: Number(item.price) || 0,
      discount_percent: 0,
      gst_percent: Number(item.gst_percent) || 0,
    }]);
    toast.success(`Added "${item.name}" to cart`);
    refocus();
  };

  const updateLine = (i, patch) => setCart(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const removeLine = (i) => setCart(prev => prev.filter((_, idx) => idx !== i));

  const addQuoteLines = (quote) => {
    const newLines = quote.lines.map((ln, idx) => ({
      lid: `g-${ln.rate_id}-${Date.now()}-${idx}`,
      kind: "service",
      ref_id: ln.rate_id,
      name: ln.label,
      hsn_code: null,
      qty: ln.qty,
      unit_price: ln.price,
      discount_percent: 0,
      gst_percent: ln.gst_percent,
    }));
    setCart(prev => [...prev, ...newLines]);
    toast.success(`Added ${newLines.length} grooming line(s) · ₹${quote.grand_total.toFixed(2)}`);
  };

  const totals = useMemo(() => {
    let sub = 0, tax = 0, ldisc = 0;
    cart.forEach(it => {
      const gross = it.qty * it.unit_price;
      const dline = gross * (it.discount_percent / 100);
      const net = gross - dline;
      sub += net;
      if (taxEnabled) tax += net * (it.gst_percent / 100);
      ldisc += dline;
    });
    const grand = sub + tax - discountAmount;
    return { sub: +sub.toFixed(2), tax: +tax.toFixed(2), ldisc: +ldisc.toFixed(2), grand: +grand.toFixed(2) };
  }, [cart, discountAmount, taxEnabled]);

  const filteredProducts = products.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.barcode?.includes(search) || p.sku?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4 -m-4 md:-m-8 p-4 md:p-6 h-[calc(100vh-4rem)] md:h-screen flex flex-col" data-testid="pos-page">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">POS Billing</h1>
        <Badge variant="secondary" className="font-mono text-xs">{cart.length} items</Badge>
        <div className="ml-auto flex items-center gap-2">
          {customer ? (
            <div className="flex items-center gap-2 px-3 h-10 rounded-md bg-muted text-sm">
              <span className="font-medium">{customer.name}</span>
              <span className="text-muted-foreground font-mono text-xs">{customer.mobile_masked || customer.mobile}</span>
              <button onClick={() => setCustomer(null)} className="hover:text-destructive"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setCustDialog(true)} data-testid="attach-customer-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Attach customer
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 grid lg:grid-cols-[1fr_440px] gap-4">
        {/* Catalog */}
        <Card className="p-4 flex flex-col min-h-0">
          <div className="flex items-center gap-2">
            <ScanBarcode className="w-4 h-4 text-primary" />
            <Input
              ref={barcodeRef}
              placeholder="Scan barcode or press Enter…"
              onKeyDown={onBarcode}
              data-testid="pos-barcode-input"
              className="h-11 font-mono"
              autoFocus
            />
            <Button variant="outline" className="h-11 shrink-0" onClick={() => setCustomOpen(true)} data-testid="pos-custom-item-btn">
              <Pencil className="w-4 h-4 mr-1" />Custom item
            </Button>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="mt-3 flex-1 flex flex-col min-h-0">
            <div className="flex items-center gap-3">
              <TabsList>
                <TabsTrigger value="products" data-testid="pos-tab-products">Products</TabsTrigger>
                <TabsTrigger value="services" data-testid="pos-tab-services">Services</TabsTrigger>
                <TabsTrigger value="grooming" data-testid="pos-tab-grooming"><Scissors className="w-3.5 h-3.5 mr-1" />Grooming</TabsTrigger>
              </TabsList>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="pos-search" />
              </div>
            </div>
            <TabsContent value="products" className="flex-1 min-h-0 overflow-y-auto pr-1 mt-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2.5">
                {filteredProducts.map(p => (
                  <button
                    key={p.id}
                    onClick={() => addProduct(p)}
                    disabled={p.stock <= 0}
                    data-testid={`pos-product-${p.id}`}
                    className="text-left rounded-lg border border-border p-3 hover:border-primary hover:-translate-y-0.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-card"
                  >
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{p.barcode || p.sku || "SKU"}</div>
                    <div className="font-semibold text-sm mt-1 line-clamp-2 min-h-[2.5em]">{p.name}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="font-heading text-lg font-bold">{inr(p.sale_price)}</span>
                      <span className={`text-xs ${p.low_stock ? "text-orange-600" : "text-muted-foreground"}`}>{p.stock}</span>
                    </div>
                  </button>
                ))}
                {filteredProducts.length === 0 && <div className="col-span-full text-sm text-muted-foreground p-4">No products. <Link to="/products" className="text-primary underline">Add some →</Link></div>}
              </div>
            </TabsContent>
            <TabsContent value="services" className="flex-1 min-h-0 overflow-y-auto pr-1 mt-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {services.map(s => (
                  <button
                    key={s.id}
                    onClick={() => addService(s)}
                    data-testid={`pos-service-${s.id}`}
                    className="text-left rounded-lg border border-border p-3 hover:border-primary bg-card"
                  >
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.category} · {s.duration_min}m</div>
                    <div className="font-semibold text-sm mt-1">{s.name}</div>
                    <div className="mt-2 font-heading text-lg font-bold">{inr(s.price)}</div>
                  </button>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="grooming" className="flex-1 min-h-0 overflow-y-auto pr-1 mt-3">
              {groomingRates.length === 0 ? (
                <div className="text-sm text-muted-foreground p-4">No grooming rates yet. <Link to="/grooming" className="text-primary underline">Set up the catalog →</Link></div>
              ) : (
                <div className="max-w-xl">
                  <GroomingConfigurator rates={groomingRates} onPriced={addQuoteLines} />
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>

        {/* Cart */}
        <Card className="p-4 flex flex-col min-h-0" data-testid="pos-cart">
          <div className="flex items-center justify-between">
            <div className="font-heading font-semibold">Cart</div>
            {cart.length > 0 && <Button variant="ghost" size="sm" onClick={() => setCart([])} data-testid="clear-cart"><Trash2 className="w-4 h-4 mr-1" />Clear</Button>}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1 divide-y divide-border">
            {cart.length === 0 && <div className="py-12 text-center text-sm text-muted-foreground">Scan a product or tap to add.</div>}
            {cart.map((it, i) => {
              const gross = it.qty * it.unit_price;
              const net = gross * (1 - it.discount_percent / 100);
              const final = net * (1 + it.gst_percent / 100);
              return (
                <div key={it.lid || `${it.ref_id}-${i}`} className="py-3" data-testid={`cart-line-${i}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{it.name}</div>
                      <div className="text-xs text-muted-foreground">{it.kind} · GST {it.gst_percent}%{it.hsn_code ? ` · HSN ${it.hsn_code}` : ""}</div>
                    </div>
                    <button onClick={() => removeLine(i)} className="text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center border border-border rounded-md">
                      <button onClick={() => updateLine(i, { qty: Math.max(1, it.qty - 1) })} className="px-2 py-1 hover:bg-muted"><Minus className="w-3 h-3" /></button>
                      <input
                        type="number"
                        value={it.qty}
                        min="1"
                        onChange={e => updateLine(i, { qty: Math.max(1, parseFloat(e.target.value) || 1) })}
                        className="w-12 text-center bg-transparent text-sm"
                        data-testid={`cart-qty-${i}`}
                      />
                      <button onClick={() => updateLine(i, { qty: it.qty + 1 })} className="px-2 py-1 hover:bg-muted"><Plus className="w-3 h-3" /></button>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      <Tag className="w-3 h-3 text-muted-foreground" />
                      <input
                        type="number"
                        value={it.discount_percent}
                        min="0" max="100"
                        onChange={e => updateLine(i, { discount_percent: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })}
                        className="w-12 border border-border rounded px-1 py-0.5 text-center"
                        data-testid={`cart-disc-${i}`}
                      />%
                    </div>
                    <div className="ml-auto font-semibold text-sm font-mono">{inr(final)}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <Separator className="my-3" />
          <div className="space-y-1 text-sm">
            <Row label="Subtotal" value={inr(totals.sub)} />
            <Row label={taxEnabled ? "Tax (GST)" : "Tax (GST) · OFF"} value={inr(totals.tax)} />
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Bill discount</span>
              <input
                type="number" value={discountAmount}
                onChange={e => setDiscountAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                className="w-24 border border-border rounded px-2 py-1 text-right"
                data-testid="pos-bill-discount"
              />
            </div>
            <Separator className="my-2" />
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Grand total</span>
              <span className="font-heading text-3xl font-black tracking-tighter">{inr(totals.grand)}</span>
            </div>
          </div>
          <Button
            className="mt-3 h-14 text-lg"
            disabled={cart.length === 0 || totals.grand <= 0}
            onClick={() => setPayOpen(true)}
            data-testid="pos-pay-btn"
          >
            <IndianRupee className="w-5 h-5 mr-2" /> Pay {inr(totals.grand)}
          </Button>
        </Card>
      </div>

      <PaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        total={totals.grand}
        cart={cart}
        discountAmount={discountAmount}
        customer={customer}
        onPaid={() => { setCart([]); setDiscountAmount(0); setCustomer(null); setPayOpen(false); api.get("/products").then(r => setProducts(r.data)); }}
      />

      <CustomerPickerDialog
        open={custDialog}
        onOpenChange={setCustDialog}
        customers={customers}
        onPicked={(c) => { setCustomer(c); setCustDialog(false); }}
        onCreated={(c) => { setCustomers(prev => [c, ...prev]); setCustomer(c); setCustDialog(false); }}
      />

      <CustomItemDialog
        open={customOpen}
        onOpenChange={setCustomOpen}
        taxEnabled={taxEnabled}
        onAdd={addCustomItem}
      />
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-mono">{value}</span>
  </div>
);

function PaymentDialog({ open, onOpenChange, total, cart, discountAmount, customer, onPaid }) {
  const [splits, setSplits] = useState([{ method: "cash", amount: 0, reference: "" }]);
  const [sendWa, setSendWa] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [walletRedeem, setWalletRedeem] = useState(0);
  const [walletBalance, setWalletBalance] = useState(0);
  const [overpayToWallet, setOverpayToWallet] = useState(true);
  const [onCredit, setOnCredit] = useState(false);
  const [creditDue, setCreditDue] = useState("");

  useEffect(() => {
    if (open) {
      setSplits([{ method: "cash", amount: total, reference: "" }]);
      setWalletRedeem(0);
      setOverpayToWallet(true);
      setOnCredit(false);
      setCreditDue("");
      api.get("/settings/loyalty").then(r => setLoyalty(r.data)).catch(() => {});
      if (customer?.id) {
        api.get(`/customers/${customer.id}`).then(r => setWalletBalance(r.data.wallet_balance || 0)).catch(() => setWalletBalance(0));
      } else setWalletBalance(0);
    }
  }, [open, total, customer]);

  const cap = loyalty?.enabled ? total * (loyalty.redeem_cap_percent || 0) / 100 : 0;
  const maxRedeem = Math.min(cap, walletBalance);
  const paid = splits.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0) + (parseFloat(walletRedeem) || 0);
  const remaining = Math.max(0, total - paid);
  const overpayment = Math.max(0, paid - total);

  const submit = async () => {
    if (onCredit && !customer) { toast.error("Attach a customer for credit billing"); return; }
    if (onCredit && remaining > 0 && !customer?.mobile && !customer?.mobile_masked) {
      toast.error("Customer mobile number is mandatory for credit notes");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        customer_id: customer?.id,
        items: cart.map(c => ({
          kind: c.kind, ref_id: c.ref_id, name: c.name, hsn_code: c.hsn_code,
          qty: c.qty, unit_price: c.unit_price, discount_percent: c.discount_percent, gst_percent: c.gst_percent,
        })),
        discount_amount: discountAmount,
        payments: splits.filter(s => parseFloat(s.amount) > 0).map(s => ({ method: s.method, amount: parseFloat(s.amount), reference: s.reference || null })),
        send_whatsapp: sendWa && !!customer,
        wallet_redeem: parseFloat(walletRedeem) || 0,
        overpayment_to_wallet: overpayToWallet,
        on_credit: onCredit && remaining > 0,
        credit_due_date: onCredit ? creditDue || null : null,
      };
      const { data } = await api.post("/invoices", payload);
      const extras = [];
      if (data.wallet_earned) extras.push(`+₹${data.wallet_earned} loyalty`);
      if (data.wallet_overpayment_credited) extras.push(`+₹${data.wallet_overpayment_credited} overpay → wallet`);
      if (data.credit_note) extras.push(`Credit note ${data.credit_note.credit_no} (₹${data.credit_note.balance})`);
      toast.success(`Invoice ${data.invoice_no} ${extras.length ? "· " + extras.join(" · ") : "created"}`);
      onPaid();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="pay-dialog">
        <DialogHeader><DialogTitle>Take payment · {inr(total)}</DialogTitle></DialogHeader>
        {customer && loyalty?.enabled && walletBalance > 0 && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 flex items-end gap-2">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-primary font-semibold">Wallet · ₹{walletBalance.toFixed(2)} available</div>
              <Label className="text-xs mt-1">Redeem amount (max {inr(maxRedeem)})</Label>
              <Input
                type="number" min="0" max={maxRedeem}
                value={walletRedeem}
                onChange={e => setWalletRedeem(Math.max(0, Math.min(maxRedeem, parseFloat(e.target.value) || 0)))}
                data-testid="wallet-redeem"
                className="mt-1"
              />
            </div>
            <Button variant="outline" size="sm" onClick={() => { setWalletRedeem(maxRedeem); setSplits([{ method: "cash", amount: Math.max(0, total - maxRedeem), reference: "" }]); }}>Use max</Button>
          </div>
        )}
        <div className="space-y-2">
          {splits.map((s, i) => (
            <div key={i} className="flex gap-2 items-end">
              <div className="w-32">
                <Label className="text-xs">Method</Label>
                <Select value={s.method} onValueChange={(v) => setSplits(prev => prev.map((p, idx) => idx === i ? { ...p, method: v } : p))}>
                  <SelectTrigger className="h-10" data-testid={`pay-method-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="wallet">Wallet</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-xs">Amount</Label>
                <Input type="number" value={s.amount} onChange={e => setSplits(prev => prev.map((p, idx) => idx === i ? { ...p, amount: e.target.value } : p))} data-testid={`pay-amount-${i}`} />
              </div>
              <div className="flex-1">
                <Label className="text-xs">Ref</Label>
                <Input value={s.reference} onChange={e => setSplits(prev => prev.map((p, idx) => idx === i ? { ...p, reference: e.target.value } : p))} placeholder="optional" />
              </div>
              {splits.length > 1 && <Button variant="ghost" size="icon" onClick={() => setSplits(prev => prev.filter((_, idx) => idx !== i))}><X className="w-4 h-4" /></Button>}
            </div>
          ))}
          <Button variant="ghost" size="sm" onClick={() => setSplits(prev => [...prev, { method: "card", amount: 0, reference: "" }])} data-testid="add-split"><Plus className="w-4 h-4 mr-1" />Split payment</Button>
          <div className="text-sm text-muted-foreground">Paid {inr(paid)} of {inr(total)} {paid < total && <span className="text-orange-600">· Balance {inr(total - paid)}</span>}{overpayment > 0 && <span className="text-emerald-600 font-semibold"> · Overpayment {inr(overpayment)}</span>}</div>
        </div>

        {customer && overpayment > 0 && (
          <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 space-y-2" data-testid="overpayment-block">
            <div className="text-sm font-semibold text-emerald-900">Overpayment detected: {inr(overpayment)}</div>
            <label className="flex items-center gap-2 text-sm text-emerald-900">
              <input type="checkbox" checked={overpayToWallet} onChange={e => setOverpayToWallet(e.target.checked)} data-testid="overpay-toggle" />
              Credit to {customer.name}'s wallet & send WhatsApp receipt
            </label>
            {!overpayToWallet && <div className="text-xs text-emerald-800">Refund the extra to the customer at the counter — no record in app.</div>}
          </div>
        )}

        {customer && remaining > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="credit-block">
            <label className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <input type="checkbox" checked={onCredit} onChange={e => setOnCredit(e.target.checked)} data-testid="credit-toggle" />
              Bill balance {inr(remaining)} on credit (pay later)
            </label>
            {onCredit && (
              <div className="flex items-center gap-2">
                <Label className="text-xs text-amber-900 shrink-0">Due date (optional)</Label>
                <Input type="date" value={creditDue} onChange={e => setCreditDue(e.target.value)} className="h-8" data-testid="credit-due" />
              </div>
            )}
            {onCredit && <div className="text-xs text-amber-800">A credit note will be auto-created. Customer mobile is mandatory (already attached: {customer.mobile_masked || customer.mobile}).</div>}
          </div>
        )}

        {customer && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={sendWa} onChange={e => setSendWa(e.target.checked)} data-testid="send-wa-toggle" />
            Send invoice via WhatsApp to {customer.name}
          </label>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || (paid <= 0 && !onCredit)} data-testid="confirm-pay-btn"><Send className="w-4 h-4 mr-2" />{busy ? "Processing…" : (onCredit && paid === 0 ? "Bill on credit" : "Complete sale")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomItemDialog({ open, onOpenChange, taxEnabled, onAdd }) {
  const [f, setF] = useState({ name: "", price: "", qty: 1, gst_percent: 0 });

  useEffect(() => {
    if (open) setF({ name: "", price: "", qty: 1, gst_percent: 0 });
  }, [open]);

  const submit = () => {
    if (!f.name?.trim()) { toast.error("Item name required"); return; }
    const price = parseFloat(f.price);
    if (!price || price <= 0) { toast.error("Enter a valid price"); return; }
    onAdd({ name: f.name.trim(), price, qty: f.qty || 1, gst_percent: taxEnabled ? (f.gst_percent || 0) : 0 });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="custom-item-dialog">
        <DialogHeader>
          <DialogTitle>Add custom item</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-muted-foreground -mt-2">Bill an off-catalog product, service or narration (no stock impact).</div>
        <div className="space-y-3 pt-2">
          <div>
            <Label className="text-xs">Item / Narration *</Label>
            <Input
              value={f.name}
              onChange={e => setF(p => ({ ...p, name: e.target.value }))}
              placeholder='e.g. "Custom grooming touch-up" / "Pet sitting"'
              autoFocus
              data-testid="custom-item-name"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Price (₹) *</Label>
              <Input
                type="number" min="0" step="0.01"
                value={f.price}
                onChange={e => setF(p => ({ ...p, price: e.target.value }))}
                data-testid="custom-item-price"
              />
            </div>
            <div>
              <Label className="text-xs">Qty</Label>
              <Input
                type="number" min="1" step="1"
                value={f.qty}
                onChange={e => setF(p => ({ ...p, qty: Math.max(1, parseInt(e.target.value) || 1) }))}
                data-testid="custom-item-qty"
              />
            </div>
            <div>
              <Label className="text-xs">GST %</Label>
              <Input
                type="number" min="0" max="28" step="1"
                value={f.gst_percent}
                disabled={!taxEnabled}
                onChange={e => setF(p => ({ ...p, gst_percent: Math.max(0, parseFloat(e.target.value) || 0) }))}
                data-testid="custom-item-gst"
              />
            </div>
          </div>
          {!taxEnabled && <div className="text-xs text-muted-foreground">Tax is OFF globally — this line will be billed without GST.</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="custom-item-add"><Plus className="w-4 h-4 mr-1" />Add to cart</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomerPickerDialog({ open, onOpenChange, customers, onPicked, onCreated }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newOne, setNewOne] = useState({ name: "", mobile: "", email: "" });

  useEffect(() => { if (!open) { setQ(""); setResults([]); } }, [open]);

  // Debounced server-side search — works for billing role: backend searches
  // the full mobile column but returns masked mobile in response.
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!q || q.length < 1) {
        // Default: show the most recent few customers passed in
        setResults((customers || []).slice(0, 25));
        return;
      }
      setLoading(true);
      try {
        const { data } = await api.get("/customers", { params: { q } });
        setResults(data.slice(0, 50));
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, customers]);

  const createNew = async () => {
    if (!newOne.name || !newOne.mobile) { toast.error("Name and mobile required"); return; }
    try {
      const { data } = await api.post("/customers", newOne);
      onCreated(data);
      setNewOne({ name: "", mobile: "", email: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Attach customer</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Input
            placeholder="Search by name or mobile number…"
            value={q}
            onChange={e => setQ(e.target.value)}
            autoFocus
            inputMode="text"
            autoComplete="off"
            data-testid="cust-search"
          />
          <div className="max-h-60 overflow-y-auto border border-border rounded-md divide-y divide-border">
            {loading && <div className="p-3 text-xs text-muted-foreground">Searching…</div>}
            {!loading && results.map(c => (
              <button key={c.id} onClick={() => onPicked(c)} className="w-full text-left p-3 hover:bg-muted text-sm" data-testid={`pick-cust-${c.id}`}>
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-muted-foreground font-mono">{c.mobile_masked || c.mobile}</div>
              </button>
            ))}
            {!loading && results.length === 0 && <div className="p-4 text-sm text-muted-foreground">{q ? "No matches." : "Start typing a name or mobile…"}</div>}
          </div>
        </div>
        <Separator />
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Or create new</div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Name" value={newOne.name} onChange={e => setNewOne(n => ({ ...n, name: e.target.value }))} data-testid="new-cust-name" />
            <Input placeholder="Mobile" value={newOne.mobile} onChange={e => setNewOne(n => ({ ...n, mobile: e.target.value }))} data-testid="new-cust-mobile" />
            <Input placeholder="Email (optional)" value={newOne.email} onChange={e => setNewOne(n => ({ ...n, email: e.target.value }))} className="col-span-2" />
          </div>
          <Button className="mt-2" onClick={createNew} data-testid="create-cust-btn"><Plus className="w-4 h-4 mr-1" />Create & attach</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
